import React, { useCallback, useState } from 'react';
import { Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { pxToRem } from '@withjoy/joykit/theme';
import {
  RevolutAmountPrefix,
  RevolutAmountDisplay,
  RevolutApyDisplay,
  RevolutAmountSubtext,
  RevolutPromoHeadline,
  RevolutPromoSubtext,
  RevolutPrimaryButton,
  RevolutSecondaryButton,
  RevolutFinePrint,
  RevolutDisclaimerToggle,
} from './TransferToBankDialog.styles';
import type { TransferPartnerConfig } from './transferPartnerConfigs';

export type RevolutTransferPromoProps = Readonly<{
  config: TransferPartnerConfig;
  amountFormatted: string;
  /** No-bank state: triggers connect-bank flow. Pending state: dismisses promo and falls through to transfer confirm. */
  onSkip?: () => void;
  /** When true, opening the partner CTA immediately calls onSkip (repeat/ready flow). When false/omitted, shows a "Continue" step first (initial/unconnected flow). */
  autoAdvanceOnCta?: boolean;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const RevolutTransferPromo: React.FC<RevolutTransferPromoProps> = ({ config, amountFormatted, onSkip, autoAdvanceOnCta, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [partnerOpened, setPartnerOpened] = useState(false);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', config.partnerId);
    openWindow(config.ctaUrl, '_blank', 'noopener,noreferrer');
    if (autoAdvanceOnCta) {
      onSkip?.();
    } else {
      setPartnerOpened(true);
    }
  }, [onBankPromoInteracted, openWindow, config.partnerId, config.ctaUrl, autoAdvanceOnCta, onSkip]);

  const handleSkip = useCallback(() => {
    onBankPromoInteracted?.('bank_partner_skip', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  const handleContinue = useCallback(() => {
    onBankPromoInteracted?.('bank_partner_continue', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  const handleToggleDisclaimer = () => setDisclaimerExpanded(prev => !prev);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={7}
      paddingTop={8}
      paddingBottom={6}
      flex={1}
      justifyContent={partnerOpened ? 'center' : undefined}
    >
      {!partnerOpened && (
        <>
          <Flex
            alignItems="baseline"
            gap={1}
            marginBottom={3}
          >
            <RevolutAmountPrefix>{tq`Up to`}</RevolutAmountPrefix>
            <RevolutApyDisplay style={{ color: '#111111' }}>{config.apyRate}</RevolutApyDisplay>
          </Flex>
          <RevolutAmountSubtext marginBottom={2}>{tq`Annual Percentage Yield (APY)¹ (variable) on your cash gifts`}</RevolutAmountSubtext>
          <RevolutPromoHeadline
            marginTop={2}
            style={{ marginBottom: pxToRem(30) }}
          >{tq`Your wedding gifts, earning up to ${config.apyRate} APY¹ — paid monthly, access anytime.`}</RevolutPromoHeadline>
        </>
      )}

      <RevolutPromoSubtext>{tq`Cash gifts received`}</RevolutPromoSubtext>
      <RevolutAmountDisplay style={{ color: '#111111', marginBottom: pxToRem(32) }}>{amountFormatted}</RevolutAmountDisplay>

      {partnerOpened ? (
        <RevolutPrimaryButton
          type="button"
          size="lg"
          shape="rounded"
          intent="neutral"
          onClick={handleContinue}
        >
          {tq`Continue`}
        </RevolutPrimaryButton>
      ) : (
        <>
          <RevolutPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            intent="neutral"
            onClick={handleOpenAccount}
          >
            {tq`Open ${config.partnerDisplayName} Account`}
          </RevolutPrimaryButton>
          <RevolutSecondaryButton
            type="button"
            size="lg"
            shape="rounded"
            variant="outline"
            intent="neutral"
            onClick={handleSkip}
          >
            {tq`Transfer to another account`}
          </RevolutSecondaryButton>
          <RevolutDisclaimerToggle
            as="button"
            type="button"
            onClick={handleToggleDisclaimer}
            aria-expanded={disclaimerExpanded}
          >{tq`Additional Terms and Disclosures.`}</RevolutDisclaimerToggle>
          {disclaimerExpanded && (
            <RevolutFinePrint
              marginTop={2}
              textAlign="left"
            >
              {tq`¹The Annual Percentage Yield (APY) is a variable rate and may change at any time. These APYs are accurate as of July 6th, 2026, and apply only to Metal plan users in good standing. Plan fees apply and cancellation fees may apply for Premium and Metal plans. Terms & Conditions apply. No min. balance to open; $0.01 min. to earn APY. 5.50% APY for balances held in a Max Rate Savings Account; 3.75% APY for any balances in other Savings Accounts. Only 1 Max Rate Savings Accounts is permitted and is limited to $10,000 USD in user-initiated deposits. The max combined balance of all Savings Accounts is $250,000 per user. Non-Metal plan users may receive different APYs than those above. Revolut is not a bank. Savings Account services provided by Cross River Bank, Member FDIC.`}
            </RevolutFinePrint>
          )}
        </>
      )}
    </Flex>
  );
};
